import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TYPES = { actif: 'Actif', passif: 'Passif', revenu: 'Revenu', depense: 'Dépense' };
const cls = 'bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white';

const AccountSelect = ({ value, accounts, onChange, testId }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)} className={`${cls} w-full`} data-testid={testId}>
    {accounts.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
  </select>
);

const AccountsTable = ({ accounts, setAccounts }) => {
  const upd = (i, k, v) => setAccounts(accounts.map((a, j) => j === i ? { ...a, [k]: v } : a));
  return (
    <div className="space-y-2">
      {accounts.map((a, i) => (
        <div key={i} className="flex gap-2 items-center" data-testid={`account-row-${i}`}>
          <input value={a.code} onChange={e => upd(i, 'code', e.target.value)} className={`${cls} w-24 font-mono`} data-testid={`account-code-${i}`} />
          <input value={a.name} onChange={e => upd(i, 'name', e.target.value)} className={`${cls} flex-1`} data-testid={`account-name-${i}`} />
          <select value={a.type} onChange={e => upd(i, 'type', e.target.value)} className={`${cls} w-28`}>
            {Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button onClick={() => setAccounts(accounts.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400" data-testid={`account-remove-${i}`}><Trash2 size={15} /></button>
        </div>
      ))}
      <button onClick={() => setAccounts([...accounts, { code: '', name: '', type: 'depense' }])}
        className="flex items-center gap-1 text-amber-400 text-sm hover:text-amber-300" data-testid="account-add"><Plus size={15} /> Ajouter un compte</button>
    </div>
  );
};

export const ChartOfAccountsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = (p) => p.then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Erreur'));
  useEffect(() => { load(axios.get(`${API_URL}/api/accounting/settings`, { headers })); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <p className="text-slate-500 text-sm">Chargement…</p>;
  const valid = data.accounts.filter(a => a.code && a.name);
  const catMap = Object.fromEntries(data.categories.map(c => [c.name, data.category_mapping[c.name] || c.account]));

  const save = () => load(axios.put(`${API_URL}/api/accounting/settings`,
    { accounts: data.accounts, mapping: data.mapping, category_mapping: catMap }, { headers })).then(() => toast.success('Plan de comptes enregistré'));
  const reset = () => window.confirm('Revenir au plan de comptes par défaut ?') &&
    load(axios.post(`${API_URL}/api/accounting/settings/reset`, {}, { headers })).then(() => toast.success('Plan par défaut rétabli'));

  return (
    <div className="space-y-6" data-testid="chart-of-accounts-tab">
      <p className="text-sm text-slate-400 max-w-3xl">Ce plan est neutre : vous associez une seule fois chaque type d'opération à un numéro de compte, et tous les exports (Sage, QuickBooks, Acomba, Excel…) le réutilisent. Ajustez les numéros pour qu'ils correspondent à ceux de votre comptable.</p>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-semibold">Comptes</h3>
          <AccountsTable accounts={data.accounts} setAccounts={(accounts) => setData({ ...data, accounts })} />
        </section>
        <div className="space-y-6">
          <section className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
            <h3 className="text-white font-semibold">Où va chaque opération</h3>
            {Object.entries(data.mapping_labels).map(([k, label]) => (
              <div key={k} className="grid grid-cols-2 gap-3 items-center">
                <span className="text-sm text-slate-300">{label}</span>
                <AccountSelect value={data.mapping[k]} accounts={valid} testId={`mapping-${k}`} onChange={v => setData({ ...data, mapping: { ...data.mapping, [k]: v } })} />
              </div>
            ))}
          </section>
          <section className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
            <h3 className="text-white font-semibold">Catégories de dépenses</h3>
            {data.categories.map(c => (
              <div key={c.name} className="grid grid-cols-2 gap-3 items-center">
                <span className="text-sm text-slate-300">{c.name}</span>
                <AccountSelect value={catMap[c.name]} accounts={valid} testId={`category-map-${c.name}`}
                  onChange={v => setData({ ...data, category_mapping: { ...catMap, [c.name]: v } })} />
              </div>
            ))}
          </section>
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={save} className="flex items-center gap-2 px-5 py-2 rounded-full bg-amber-500 text-black font-semibold text-sm hover:bg-amber-400" data-testid="accounts-save"><Save size={15} /> Enregistrer</button>
        <button onClick={reset} className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 text-slate-300 text-sm hover:border-slate-500" data-testid="accounts-reset"><RotateCcw size={15} /> Plan par défaut</button>
      </div>
    </div>
  );
};
